<script lang="ts">
  import {
    Veterinaria,
    type Mascota, type Propietario, type NotaExpedienteVet, type Vacuna, type PesoRegistro,
  } from '../lib/api';

  let busqueda = $state('');
  let resultados = $state<Mascota[]>([]);
  let propietarios = $state<Propietario[]>([]);
  let mascota = $state<Mascota | null>(null);
  let notas = $state<NotaExpedienteVet[]>([]);
  let vacunas = $state<Vacuna[]>([]);
  let pesos = $state<PesoRegistro[]>([]);
  let subTab = $state<'resumen' | 'evolucion' | 'vacunas' | 'peso'>('resumen');
  let buscando = $state(false);
  let cargando = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);

  let notaNueva = $state<{ autor: string; contenido: string; tipo: NotaExpedienteVet['tipo'] }>({
    autor: '', contenido: '', tipo: 'EVOLUCION',
  });

  function nombreDueno(id?: string | null) {
    return propietarios.find(p => p.id === id)?.nombre ?? '—';
  }

  async function buscarPorNombre() {
    const q = busqueda.trim();
    if (q.length < 2) { error = 'Escriba al menos 2 letras del nombre de la mascota.'; return; }
    buscando = true;
    error = null;
    info = null;
    mascota = null;
    try {
      if (propietarios.length === 0) {
        propietarios = await Veterinaria.propietarios().catch(() => []);
      }
      resultados = await Veterinaria.mascotas(q);
      if (resultados.length === 1) await abrirMascota(resultados[0]);
      else if (resultados.length === 0) info = 'No hay mascotas con ese nombre.';
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      buscando = false;
    }
  }

  async function abrirMascota(m: Mascota) {
    if (!m.id) return;
    mascota = m;
    subTab = 'resumen';
    cargando = true;
    error = null;
    try {
      const [n, v, p] = await Promise.all([
        Veterinaria.notasExpediente(m.id).catch(() => []),
        Veterinaria.vacunas(m.id).catch(() => []),
        Veterinaria.pesos(m.id).catch(() => []),
      ]);
      notas = n;
      vacunas = v;
      pesos = p;
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      cargando = false;
    }
  }

  async function guardarNota(e: SubmitEvent) {
    e.preventDefault();
    if (!mascota?.id || !notaNueva.contenido.trim()) return;
    try {
      const creada = await Veterinaria.agregarNotaExpediente(mascota.id, {
        autor: notaNueva.autor.trim() || 'Veterinario demo',
        contenido: notaNueva.contenido.trim(),
        tipo: notaNueva.tipo ?? 'EVOLUCION',
      });
      notas = [creada, ...notas];
      notaNueva = { autor: notaNueva.autor, contenido: '', tipo: 'EVOLUCION' };
      info = 'Nota agregada al expediente.';
      error = null;
    } catch (e: any) {
      error = e?.message ?? String(e);
    }
  }

  function fmtFecha(iso?: string | null) {
    if (!iso) return '—';
    return iso.slice(0, 16).replace('T', ' ');
  }
</script>

<section class="exp-vet">
  <header>
    <div>
      <h2>Expediente clínico — mascota</h2>
      <p class="hint">Busque por <strong>nombre de la mascota</strong> (Rocky, Misha…). No usa CUI ni expediente humano.</p>
    </div>
  </header>

  {#if error}<p class="msg err">{error}</p>{/if}
  {#if info}<p class="msg ok">{info}</p>{/if}

  <div class="buscar card">
    <label>
      Nombre de la mascota
      <div class="row">
        <input
          placeholder="Ej. Rocky"
          bind:value={busqueda}
          onkeydown={(e) => e.key === 'Enter' && (e.preventDefault(), buscarPorNombre())}
        />
        <button type="button" disabled={buscando} onclick={buscarPorNombre}>
          {buscando ? 'Buscando…' : 'Buscar'}
        </button>
      </div>
    </label>

    {#if resultados.length > 1 && !mascota}
      <ul class="resultados">
        {#each resultados as m (m.id)}
          <li>
            <button type="button" class="pick" onclick={() => abrirMascota(m)}>
              <strong>{m.nombre}</strong>
              <span>{m.especie}{m.raza ? ` · ${m.raza}` : ''} — Dueño: {nombreDueno(m.propietarioId)}</span>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  {#if mascota}
    <div class="ficha card">
      <div class="ficha-head">
        <div>
          <h3>{mascota.nombre}</h3>
          <p class="meta">
            {mascota.especie}{mascota.raza ? ` · ${mascota.raza}` : ''} · {mascota.sexo}
            · Dueño: {nombreDueno(mascota.propietarioId)}
            {#if mascota.pesoActual} · {mascota.pesoActual} kg{/if}
          </p>
        </div>
        <button type="button" class="link" onclick={() => { mascota = null; resultados = []; }}>Cambiar mascota</button>
      </div>

      <nav class="tabs">
        {#each [
          ['resumen', 'Resumen'],
          ['evolucion', 'Evolución'],
          ['vacunas', 'Vacunas'],
          ['peso', 'Peso'],
        ] as [id, label]}
          <button type="button" class:active={subTab === id} onclick={() => subTab = id as typeof subTab}>{label}</button>
        {/each}
      </nav>

      {#if cargando}
        <p class="muted">Cargando expediente…</p>
      {:else if subTab === 'resumen'}
        <dl class="resumen">
          <dt>Especie / raza</dt><dd>{mascota.especie} · {mascota.raza ?? '—'}</dd>
          <dt>Microchip</dt><dd>{mascota.microchip ?? '—'}</dd>
          <dt>Alergias</dt><dd>{mascota.alergias ?? 'Ninguna registrada'}</dd>
          <dt>Último peso</dt><dd>{mascota.pesoActual ?? '—'} kg</dd>
          <dt>Notas clínicas</dt><dd>{notas.length}</dd>
          <dt>Vacunas</dt><dd>{vacunas.length}</dd>
        </dl>
      {:else if subTab === 'evolucion'}
        <form class="nota-form" onsubmit={guardarNota}>
          <div class="row">
            <input placeholder="Veterinario" bind:value={notaNueva.autor} />
            <select bind:value={notaNueva.tipo}>
              <option value="EVOLUCION">Evolución</option>
              <option value="ANAMNESIS">Anamnesis</option>
              <option value="PROCEDIMIENTO">Procedimiento</option>
              <option value="ALTA">Alta / seguimiento</option>
            </select>
          </div>
          <textarea rows="3" placeholder="Nota clínica…" bind:value={notaNueva.contenido} required></textarea>
          <button type="submit">Agregar nota</button>
        </form>
        <ul class="notas">
          {#each notas as n (n.id)}
            <li>
              <header>
                <strong>{n.tipo ?? 'EVOLUCION'}</strong>
                <span>{n.autor ?? '—'} · {fmtFecha(n.createdAt)}</span>
              </header>
              <p>{n.contenido}</p>
            </li>
          {:else}
            <li class="muted">Sin notas en el expediente.</li>
          {/each}
        </ul>
      {:else if subTab === 'vacunas'}
        <table>
          <thead><tr><th>Vacuna</th><th>Aplicada</th><th>Próxima</th></tr></thead>
          <tbody>
            {#each vacunas as v (v.id)}
              <tr><td>{v.nombre}</td><td>{v.fechaAplicacion}</td><td>{v.proximaDosis ?? '—'}</td></tr>
            {:else}
              <tr><td colspan="3" class="muted">Sin vacunas en carné.</td></tr>
            {/each}
          </tbody>
        </table>
      {:else}
        <table>
          <thead><tr><th>Fecha</th><th>Peso (kg)</th><th>Nota</th></tr></thead>
          <tbody>
            {#each pesos as p (p.id)}
              <tr><td>{p.fecha}</td><td>{p.pesoKg}</td><td>{p.nota ?? '—'}</td></tr>
            {:else}
              <tr><td colspan="3" class="muted">Sin registros de peso.</td></tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>
  {/if}
</section>

<style>
  .exp-vet { display: flex; flex-direction: column; gap: 1rem; }
  header h2 { margin: 0; color: var(--aqua-deep); }
  .hint { margin: 0.35rem 0 0; color: var(--muted); font-size: 0.88rem; max-width: 52ch; }
  .card { border: 1px solid var(--line); border-radius: 10px; padding: 1rem; background: #fff; }
  .row { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; }
  label { display: flex; flex-direction: column; gap: 0.35rem; font-size: 0.9rem; }
  input, select, textarea {
    padding: 0.45rem 0.6rem; border: 1px solid var(--line); border-radius: 6px; font: inherit;
  }
  input { flex: 1; min-width: 160px; }
  button[type=submit], .buscar > label button {
    padding: 0.5rem 0.9rem; background: var(--aqua); color: #fff; border: 0; border-radius: 6px;
    font-weight: 600; cursor: pointer;
  }
  .link { border: 0; background: transparent; color: var(--aqua-dark); cursor: pointer; text-decoration: underline; }
  .resultados { list-style: none; margin: 0.75rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 0.35rem; }
  .pick {
    width: 100%; text-align: left; padding: 0.55rem 0.65rem; border: 1px solid var(--line);
    border-radius: 8px; background: var(--aqua-soft); cursor: pointer; font: inherit;
  }
  .pick strong { display: block; }
  .pick span { font-size: 0.82rem; color: var(--muted); }
  .ficha-head { display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start; }
  .ficha-head h3 { margin: 0; font-size: 1.25rem; }
  .meta { margin: 0.25rem 0 0; color: var(--muted); font-size: 0.85rem; }
  .tabs { display: flex; gap: 0.35rem; flex-wrap: wrap; margin: 1rem 0 0.75rem; }
  .tabs button {
    padding: 0.35rem 0.75rem; border: 1px solid var(--line); background: #fff;
    border-radius: 999px; cursor: pointer; font: inherit; color: var(--aqua-deep);
  }
  .tabs button.active { background: var(--aqua); color: #fff; border-color: var(--aqua); }
  .resumen { display: grid; grid-template-columns: 140px 1fr; gap: 0.35rem 1rem; margin: 0; }
  .resumen dt { color: var(--muted); font-size: 0.85rem; }
  .resumen dd { margin: 0; }
  .nota-form { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
  .notas { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.65rem; }
  .notas li { border: 1px solid var(--line); border-radius: 8px; padding: 0.65rem 0.75rem; }
  .notas header { display: flex; justify-content: space-between; gap: 0.5rem; font-size: 0.78rem; color: var(--muted); }
  .notas p { margin: 0.35rem 0 0; white-space: pre-wrap; }
  table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
  th, td { text-align: left; padding: 0.4rem 0.5rem; border-bottom: 1px solid var(--line); }
  th { color: var(--muted); }
  .muted { color: var(--muted); text-align: center; padding: 0.75rem; }
  .msg { padding: 0.55rem 0.8rem; border-radius: 6px; margin: 0; }
  .msg.err { background: #fdeaea; color: #b23a3a; }
  .msg.ok { background: #e3f6e8; color: #1b7a3d; }
</style>
